import { gsap } from 'gsap'

import Common from 'js/_core/Common'
import ScrollAnchor from 'js/modules/_base/events/scroll/ScrollAnchor'

import FooterMainScrollProgressAnimation from 'js/modules/projects/effects/FooterMainScrollProgressAnimation'
import ElementClone from 'js/modules/_base/effects/helper/ElementClone'
import ElementMask from 'js/modules/_base/effects/helper/ElementMask'
import TextSplit from 'js/modules/_base/effects/helper/TextSplit'
import CssHover from 'js/modules/_base/events/mouse/hover/CssHover'

// inView items
import TitleFadeAInViewItem from 'js/modules/projects/effects/TitleFadeAInViewItem'
import TitleMaskAInViewItem from 'js/modules/projects/effects/TitleMaskAInViewItem'
import LineAInViewItem from 'js/modules/projects/effects/LineAInViewItem'
import SideStickySectionTextInViewAnimation from 'js/modules/projects/effects/SideStickySectionTextInViewAnimation'
import BtnMoreInViewItem from 'js/modules/projects/effects/BtnMoreInViewItem'
import FooterContactInViewItem from 'js/modules/projects/effects/FooterContactInViewItem'

import SubPageHomeFVAnimation from 'js/modules/projects/effects/SubPageHomeFVAnimation'

// hovers
import TextAlpha1Hover from 'js/modules/projects/events/mouse/hover/TextAlpha1Hover'
import TextAlpha2Hover from 'js/modules/projects/events/mouse/hover/TextAlpha2Hover'
import TextAlpha3Hover from 'js/modules/projects/events/mouse/hover/TextAlpha3Hover'
import Btn1Hover from 'js/modules/projects/events/mouse/hover/Btn1Hover'
import Btn2Hover from 'js/modules/projects/events/mouse/hover/Btn2Hover'
import Btn3Hover from 'js/modules/projects/events/mouse/hover/Btn3Hover'
import Banner1Hover from 'js/modules/projects/events/mouse/hover/Banner1Hover'
import Banner2Hover from 'js/modules/projects/events/mouse/hover/Banner2Hover'
import Panel1Hover from 'js/modules/projects/events/mouse/hover/Panel1Hover'
import Pager1Hover from 'js/modules/projects/events/mouse/hover/Pager1Hover'

export default class ViewController {
  constructor(content) {
    this.debug = Common.debug
    this.id = content.dataset['page']
    this.pageName = null

    // ページ別のコンテンツroot
    this.$content = content
    this._set$()

    this.contentScrollAnchor = new ScrollAnchor({
      $contentRoot: this.$content,
      useSmoothScroll: true,
    })

    this.contentElementMask = new ElementMask({
      $contentRoot: this.$content,
    })

    this.contentTextSplit = new TextSplit({
      $contentRoot: this.$content,
    })

    this.contentElementClone = new ElementClone({
      $contentRoot: this.$content,
      $names: {
        item: '[data-clone-i],.split_inner',
      },
    })

    this.contentCssHover = new CssHover({
      $contentRoot: this.$content,
    })

    if (!Common.detection.isMobileLayout) {
      this.footerMainScrollProgressAnimation = new FooterMainScrollProgressAnimation({
        $contentRoot: this.$content,
      })
    }

    this.titleFadeAInViewItem = new TitleFadeAInViewItem({
      $contentRoot: this.$content,
    })

    this.titleMaskAInViewItem = new TitleMaskAInViewItem({
      $contentRoot: this.$content,
    })

    this.lineAInViewItem = new LineAInViewItem({
      $contentRoot: this.$content,
    })

    this.btnMoreInViewItem = new BtnMoreInViewItem({
      $contentRoot: this.$content,
    })

    this.footerContactInViewItem = new FooterContactInViewItem({
      $contentRoot: this.$content,
    })

    if (!Common.detection.isMobileLayout) {
      this.sideStickySectionTextInViewAnimation = new SideStickySectionTextInViewAnimation({
        $contentRoot: this.$content,
      })
    }

    if (this.id !== 'Home') {
      this.subPageHomeFVAnimation = new SubPageHomeFVAnimation({
        $contentRoot: this.$content,
      })
    }

    if (Common.useMouseHover) {
      this.textAlpha1Hover = new TextAlpha1Hover({
        $contentRoot: this.$content,
      })
      this.textAlpha1Hover.init()

      this.textAlpha2Hover = new TextAlpha2Hover({
        $contentRoot: this.$content,
      })
      this.textAlpha2Hover.init()

      this.textAlpha3Hover = new TextAlpha3Hover({
        $contentRoot: this.$content,
      })
      this.textAlpha3Hover.init()

      this.btn1Hover = new Btn1Hover({
        $contentRoot: this.$content,
      })
      this.btn1Hover.init()

      this.btn2Hover = new Btn2Hover({
        $contentRoot: this.$content,
      })
      this.btn2Hover.init()

      this.btn3Hover = new Btn3Hover({
        $contentRoot: this.$content,
      })
      this.btn3Hover.init()

      this.panel1Hover = new Panel1Hover({
        $contentRoot: this.$content,
      })
      this.panel1Hover.init()

      this.banner1Hover = new Banner1Hover({
        $contentRoot: this.$content,
      })
      this.banner1Hover.init()

      this.banner2Hover = new Banner2Hover({
        $contentRoot: this.$content,
      })
      this.banner2Hover.init()

      this.pager1Hover = new Pager1Hover({
        $contentRoot: this.$content,
      })
      this.pager1Hover.init()
    }
  }

  async willLoad() {
    if (this.debug) {
      this.pageName ? console.log(`-> ${this.pageName} willLoad`) : console.log(`-> willLoad`)
    }
  }

  async didLoad() {
    if (this.debug) {
      this.pageName ? console.log(`-> ${this.pageName} didLoad`) : console.log(`-> didLoad`)
    }
  }

  async beforeEnter() {
    if (this.debug) {
      this.pageName ? console.log(`-> ${this.pageName} beforeEnter`) : console.log(`-> beforeEnter`)
    }
    this.contentElementMask?.init()
    this.contentTextSplit?.init()
    this.contentScrollAnchor?.init()
    this.contentElementClone?.init()
    this.contentCssHover?.init()
    this.titleFadeAInViewItem?.init()
    this.titleMaskAInViewItem?.init()
    this.lineAInViewItem?.init()
    this.btnMoreInViewItem?.init()
    this.footerContactInViewItem?.init()
    Common.pageCurrentChecker.pageEnter(this.$content, true)
    Common.langSwitcher?.pageEnter()
    this.subPageHomeFVAnimation?.init()
  }

  async enter() {
    if (this.debug) {
      this.pageName ? console.log(`-> ${this.pageName} enter`) : console.log(`-> enter`)
    }

    Common.headerManager?.pageEnter(this.$content, this.id)
    Common.headerColor?.pageEnter(this.$content)
    Common.webgl?.enter({
      pageId: this.id,
      $contentRoot: this.$content,
    })

    this.footerMainScrollProgressAnimation?.init()
    this.sideStickySectionTextInViewAnimation?.init()

    this.titleFadeAInViewItem?.run()
    this.titleMaskAInViewItem?.run()
    this.lineAInViewItem?.run()
    this.btnMoreInViewItem?.run()
    this.footerContactInViewItem?.run()

    Common.scroll.start()
    Common.iomEnable = true

    gsap.delayedCall(0.25, () => {
      this.subPageHomeFVAnimation?.run()
    })
  }

  async afterEnter() {
    if (this.debug) {
      this.pageName ? console.log(`-> ${this.pageName} afterEnter`) : console.log(`-> afterEnter`)
    }

    Common.isPageTransitioning = false
    Common.isStartup = false
    document.body.classList.add('is-loaded')
  }

  async beforeLeave() {
    if (this.debug) {
      this.pageName ? console.log(`-> ${this.pageName} beforeLeave`) : console.log(`-> beforeLeave`)
    }

    Common.headerManager?.pageLeave()
    Common.headerColor?.pageLeave()
    this.textAlpha1Hover?.destroy()
    this.textAlpha2Hover?.destroy()
    this.textAlpha3Hover?.destroy()
    this.btn1Hover?.destroy()
    this.btn2Hover?.destroy()
    this.btn3Hover?.destroy()
    this.banner1Hover?.destroy()
    this.banner2Hover?.destroy()
    this.panel1Hover?.destroy()
    this.pager1Hover?.destroy()

    Common.iomEnable = false
    Common.scroll.stop()
    this.contentScrollAnchor?.destroy()

    this.lineAInViewItem?.destroy()
    this.titleMaskAInViewItem?.destroy()
    this.titleFadeAInViewItem?.destroy()
    this.footerContactInViewItem?.destroy()
    this.footerMainScrollProgressAnimation?.destroy()
    this.contentCssHover?.destroy()

    Common.pageCurrentChecker.pageEnter(this.$content, true)
    if (Common.gNavDropdownController) {
      Common.gNavDropdownController.closeAllDropdowns()
    }
  }

  async leave() {
    if (Common.webgl) {
      Common.webgl.leave({
        pageId: this.id,
      })
    }

    if (Common.spMenu) {
      Common.spMenu.close(false)
    }
  }

  async afterLeave() {}

  _set$() {}

  addHandlers() {
    if (Common.resize) {
      Common.resize.addHandler(() => {
        this.onResize()
      }, 'ViewControllerResize')
    }
  }

  removeHandlers() {
    if (Common.resize) {
      Common.resize.removeHandler('ViewControllerResize')
    }
  }

  onResize() {}
}